import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { INTERNAL_KYC_UI } from '../../lib/featureFlags';
import { Separator } from '../../components/ui/separator';
import { api } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { 
  UserCircle, Plus, MagnifyingGlass, CheckCircle, Clock, Warning, 
  Buildings, Briefcase, Bank, Wallet, Shield, PencilSimple, Trash,
  EnvelopeSimple, Phone, MapPin, Globe, IdentificationCard, CaretRight,
  User, Users, FloppyDisk, ArrowLeft, X, Info, Eye, ChartLineUp, 
  CurrencyEur, ListBullets, Coins, Calendar, ArrowsClockwise,
  Monitor, DeviceMobile, Export, Download, Check, XCircle, FileText
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { AdminPageHeader, AdminKpi, AdminKpiStrip } from '../../components/admin/AdminPageHeader';
import AdminToolbar from '../../components/admin/AdminToolbar';

const partyTypeConfig = {
  investor_individual: { label: 'Individual Investor', icon: User, color: 'bg-blue-100 text-blue-700' },
  investor_institutional: { label: 'Institutional Investor', icon: Buildings, color: 'bg-purple-100 text-purple-700' },
  developer: { label: 'Developer', icon: Briefcase, color: 'bg-green-100 text-green-700' },
  sponsor: { label: 'Sponsor', icon: Briefcase, color: 'bg-emerald-100 text-emerald-700' },
  issuer: { label: 'Issuer', icon: Buildings, color: 'bg-indigo-100 text-indigo-700' },
  property_owner: { label: 'Property Owner', icon: Buildings, color: 'bg-amber-100 text-amber-700' },
  service_provider: { label: 'Service Provider', icon: Briefcase, color: 'bg-gray-100 text-gray-700' },
  platform: { label: 'Platform', icon: Globe, color: 'bg-pink-100 text-pink-700' },
  platform_admin: { label: 'Platform Admin', icon: Shield, color: 'bg-red-100 text-red-700' },
};

const statusConfig = {
  pending_verification: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  suspended: { label: 'Suspended', color: 'bg-red-100 text-red-700', icon: Warning },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-700', icon: Warning },
};

const kycLevelConfig = {
  none: { label: 'None', color: 'bg-gray-100 text-gray-600' },
  basic: { label: 'Basic', color: 'bg-blue-100 text-blue-700' },
  standard: { label: 'Standard', color: 'bg-green-100 text-green-700' },
  enhanced: { label: 'Enhanced', color: 'bg-purple-100 text-purple-700' },
  verified: { label: 'Verified', color: 'bg-emerald-100 text-emerald-700' },
};

const accreditationConfig = {
  not_applicable: { label: 'N/A', color: 'bg-gray-100 text-gray-600' },
  retail: { label: 'Retail', color: 'bg-blue-100 text-blue-700' },
  sophisticated: { label: 'Sophisticated', color: 'bg-green-100 text-green-700' },
  professional: { label: 'Professional', color: 'bg-purple-100 text-purple-700' },
  institutional: { label: 'Institutional', color: 'bg-indigo-100 text-indigo-700' },
};

/**
 * Party Card Component - Mobile optimized
 */
const PartyCard = ({ party, onClick }) => {
  const typeConfig = partyTypeConfig[party.party_type] || partyTypeConfig.investor_individual;
  const status = statusConfig[party.status] || statusConfig.pending_verification;
  const kyc = kycLevelConfig[party.kyc_level] || kycLevelConfig.none;
  const TypeIcon = typeConfig.icon;
  const StatusIcon = status.icon;

  return (
    <Card 
      className="bg-white rounded-lg sm:rounded-xl border border-black/[0.04] p-3 sm:p-5 cursor-pointer hover:border-primary/20 hover:shadow-md transition-all"
      onClick={() => onClick(party)}
      data-testid={`party-card-${party.code}`}
    >
      <div className="flex items-start justify-between mb-2 sm:mb-3 gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className={cn('p-2 sm:p-2.5 rounded-lg sm:rounded-xl flex-shrink-0', typeConfig.color)}>
            <TypeIcon className="h-4 w-4 sm:h-5 sm:w-5" weight="duotone" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <span className="font-mono text-[10px] sm:text-xs text-foreground-muted">{party.code}</span>
              <Badge className={cn('text-[10px] sm:text-xs px-1.5 sm:px-2', status.color)}>
                <StatusIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                {status.label}
              </Badge>
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-foreground truncate">{party.display_name}</h3>
          </div>
        </div>
        {INTERNAL_KYC_UI && (
          <Badge className={cn('text-[10px] sm:text-xs px-1.5 sm:px-2 flex-shrink-0 hidden sm:flex', kyc.color)}>
            KYC: {kyc.label}
          </Badge>
        )}
      </div>
      
      <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-foreground-muted mb-2 sm:mb-3">
        <Badge className={cn('text-[10px] sm:text-xs', typeConfig.color)}>{typeConfig.label}</Badge>
        {party.contact?.email && (
          <span className="flex items-center gap-1 truncate">
            <EnvelopeSimple className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{party.contact.email}</span>
          </span>
        )}
      </div>
      
      <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-black/[0.04]">
        <span className="text-[10px] sm:text-xs text-foreground-muted">
          {new Date(party.created_at).toLocaleDateString()}
        </span>
        <div className="flex items-center gap-1 text-xs sm:text-sm text-primary">
          <span className="hidden sm:inline">View Details</span>
          <CaretRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </div>
      </div>
    </Card>
  );
};

/**
 * Create/Edit Party Dialog
 */
const PartyFormDialog = ({ open, onOpenChange, party, onSave }) => {
  const isEdit = !!party?.id;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [formData, setFormData] = useState({
    party_type: 'investor_individual',
    display_name: '',
    legal_name: '',
    // Contact
    email: '',
    phone: '',
    secondary_email: '',
    secondary_phone: '',
    // Address
    street_line_1: '',
    street_line_2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country_code: '',
    // Tax/Legal
    tax_residency_country: '',
    tax_id: '',
    // Company fields
    company_registration_number: '',
    company_jurisdiction: '',
    website: '',
    track_record_summary: '',
    // Meta
    notes: '',
    tags: '',
    // Edit reason
    reason: '',
  });

  useEffect(() => {
    if (party) {
      setFormData({
        party_type: party.party_type || 'investor_individual',
        display_name: party.display_name || '',
        legal_name: party.legal_name || '',
        email: party.contact?.email || '',
        phone: party.contact?.phone || '',
        secondary_email: party.contact?.secondary_email || '',
        secondary_phone: party.contact?.secondary_phone || '',
        // Address from V2 address object
        street_line_1: party.address?.street_line_1 || party.address?.street || '',
        street_line_2: party.address?.street_line_2 || '',
        city: party.address?.city || '',
        state_province: party.address?.state_province || '',
        postal_code: party.address?.postal_code || '',
        country_code: party.address?.country_code || party.address?.country || '',
        // Tax info - from multiple possible sources
        tax_residency_country: party.tax_residency_country || '',
        tax_id: party.tax_id || '',
        // Company fields
        company_registration_number: party.company_registration_number || '',
        company_jurisdiction: party.company_jurisdiction || '',
        website: party.website || '',
        track_record_summary: party.track_record_summary || '',
        // Meta
        notes: party.notes || '',
        tags: party.tags?.join(', ') || '',
        reason: '',
      });
    } else {
      setFormData({
        party_type: 'investor_individual',
        display_name: '',
        legal_name: '',
        email: '',
        phone: '',
        secondary_email: '',
        secondary_phone: '',
        street_line_1: '',
        street_line_2: '',
        city: '',
        state_province: '',
        postal_code: '',
        country_code: '',
        tax_residency_country: '',
        tax_id: '',
        company_registration_number: '',
        company_jurisdiction: '',
        website: '',
        track_record_summary: '',
        notes: '',
        tags: '',
        reason: '',
      });
    }
    setActiveTab('basic');
  }, [party, open]);

  const handleSubmit = async () => {
    if (!formData.display_name.trim()) {
      toast.error('Display name is required');
      return;
    }
    if (isEdit && !formData.reason.trim()) {
      toast.error('Reason for change is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        display_name: formData.display_name,
        legal_name: formData.legal_name || null,
        contact: {
          email: formData.email || null,
          phone: formData.phone || null,
          secondary_email: formData.secondary_email || null,
          secondary_phone: formData.secondary_phone || null,
        },
        address: {
          street_line_1: formData.street_line_1 || null,
          street_line_2: formData.street_line_2 || null,
          city: formData.city || null,
          state_province: formData.state_province || null,
          postal_code: formData.postal_code || null,
          country_code: formData.country_code || null,
        },
        tax_residency_country: formData.tax_residency_country || null,
        tax_id: formData.tax_id || null,
        company_registration_number: formData.company_registration_number || null,
        company_jurisdiction: formData.company_jurisdiction || null,
        website: formData.website || null,
        track_record_summary: formData.track_record_summary || null,
        notes: formData.notes || null,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : null,
      };

      if (isEdit) {
        payload.reason = formData.reason;
        await api.put(`/admin/v2/parties/${party.id}`, payload);
        toast.success('Party updated successfully');
      } else {
        payload.party_type = formData.party_type;
        await api.post('/admin/v2/parties', payload);
        toast.success('Party created successfully');
      }
      
      onSave();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save party');
    } finally {
      setLoading(false);
    }
  };

  const isCompanyType = ['developer', 'sponsor', 'issuer', 'property_owner', 'investor_institutional', 'service_provider'].includes(formData.party_type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Party' : 'Create New Party'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update party information. All changes are audited.' : 'Add a new investor, developer, or other party to the system.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            <TabsTrigger value="address">Address</TabsTrigger>
            <TabsTrigger value="company">Company/Tax</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Party Type *</Label>
                <Select value={formData.party_type} onValueChange={(v) => setFormData({...formData, party_type: v})} disabled={isEdit}>
                  <SelectTrigger data-testid="party-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(partyTypeConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Display Name *</Label>
                <Input
                  value={formData.display_name}
                  onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                  placeholder="Name shown in UI"
                  data-testid="display-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Legal Name</Label>
              <Input
                value={formData.legal_name}
                onChange={(e) => setFormData({...formData, legal_name: e.target.value})}
                placeholder="Official legal name (if different)"
                data-testid="legal-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Internal Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Internal notes about this party..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Tags (comma separated)</Label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                placeholder="vip, early-investor, developer-partner"
              />
            </div>
          </TabsContent>

          <TabsContent value="contact" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="email@example.com"
                  data-testid="email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Primary Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+351 912 345 678"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Secondary Email</Label>
                <Input
                  type="email"
                  value={formData.secondary_email}
                  onChange={(e) => setFormData({...formData, secondary_email: e.target.value})}
                  placeholder="alternate@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Secondary Phone</Label>
                <Input
                  value={formData.secondary_phone}
                  onChange={(e) => setFormData({...formData, secondary_phone: e.target.value})}
                  placeholder="+351 913 456 789"
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="address" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Street Address Line 1</Label>
              <Input
                value={formData.street_line_1}
                onChange={(e) => setFormData({...formData, street_line_1: e.target.value})}
                placeholder="123 Main Street"
              />
            </div>
            <div className="space-y-2">
              <Label>Street Address Line 2</Label>
              <Input
                value={formData.street_line_2}
                onChange={(e) => setFormData({...formData, street_line_2: e.target.value})}
                placeholder="Apt 4B"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder="Lisbon"
                />
              </div>
              <div className="space-y-2">
                <Label>State/Province</Label>
                <Input
                  value={formData.state_province}
                  onChange={(e) => setFormData({...formData, state_province: e.target.value})}
                  placeholder="Lisboa"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Postal Code</Label>
                <Input
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  placeholder="1200-001"
                />
              </div>
              <div className="space-y-2">
                <Label>Country Code</Label>
                <Input
                  value={formData.country_code}
                  onChange={(e) => setFormData({...formData, country_code: e.target.value})}
                  placeholder="PT"
                  maxLength={2}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="company" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tax Residency Country</Label>
                <Input
                  value={formData.tax_residency_country}
                  onChange={(e) => setFormData({...formData, tax_residency_country: e.target.value})}
                  placeholder="PT"
                  maxLength={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Tax ID</Label>
                <Input
                  value={formData.tax_id}
                  onChange={(e) => setFormData({...formData, tax_id: e.target.value})}
                  placeholder="Tax identification number"
                />
              </div>
            </div>

            {isCompanyType && (
              <>
                <Separator className="my-4" />
                <h4 className="text-sm font-medium text-foreground-muted">Company Information</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Registration Number</Label>
                    <Input
                      value={formData.company_registration_number}
                      onChange={(e) => setFormData({...formData, company_registration_number: e.target.value})}
                      placeholder="PT516234567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Jurisdiction</Label>
                    <Input
                      value={formData.company_jurisdiction}
                      onChange={(e) => setFormData({...formData, company_jurisdiction: e.target.value})}
                      placeholder="Portugal"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    placeholder="https://example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Track Record Summary</Label>
                  <Textarea
                    value={formData.track_record_summary}
                    onChange={(e) => setFormData({...formData, track_record_summary: e.target.value})}
                    placeholder="Summary of company track record, previous projects, etc."
                    rows={4}
                  />
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>

        {isEdit && (
          <div className="mt-4 p-3 bg-amber-50 rounded-lg">
            <Label className="text-amber-700">Reason for Change *</Label>
            <Input
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              placeholder="Describe why this change is being made (min 5 chars)"
              className="mt-2"
              data-testid="reason-input"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="save-party-btn">
            {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Party'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Party Detail Dialog - Enhanced with Holdings & Transactions
 */
const PartyDetailDialog = ({ party, open, onOpenChange, onRefresh, onViewProfile, onEdit }) => {
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [kycFormOpen, setKycFormOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [kycData, setKycData] = useState({ kyc_level: 'basic', reason: '' });
  const [holdings, setHoldings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loadingHoldings, setLoadingHoldings] = useState(false);
  const [loadingTx, setLoadingTx] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch holdings when party changes
  useEffect(() => {
    if (party && open && party.party_type?.startsWith('investor_')) {
      fetchHoldings();
      fetchTransactions();
    }
  }, [party, open]);

  const fetchHoldings = async () => {
    if (!party) return;
    setLoadingHoldings(true);
    try {
      const res = await api.get(`/admin/v2/parties/${party.id}/holdings`);
      setHoldings(res.data.holdings || []);
    } catch (err) {
      console.error('Failed to load holdings', err);
    } finally {
      setLoadingHoldings(false);
    }
  };

  const fetchTransactions = async () => {
    if (!party) return;
    setLoadingTx(true);
    try {
      const res = await api.get(`/admin/v2/parties/${party.id}/transactions`);
      setTransactions(res.data.transactions || []);
    } catch (err) {
      console.error('Failed to load transactions', err);
    } finally {
      setLoadingTx(false);
    }
  };

  if (!party) return null;

  const typeConfig = partyTypeConfig[party.party_type] || partyTypeConfig.investor_individual;
  const status = statusConfig[party.status] || statusConfig.pending_verification;
  const kyc = kycLevelConfig[party.kyc_level] || kycLevelConfig.none;
  const accreditation = accreditationConfig[party.accreditation_status] || accreditationConfig.not_applicable;
  const TypeIcon = typeConfig.icon;
  
  // Check if this is an investor type
  const isInvestor = party.party_type?.startsWith('investor_');

  const handleActivate = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/v2/parties/${party.id}/status`, {
        new_status: 'active',
        reason: 'Admin activation'
      });
      toast.success('Party activated');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateKYC = async () => {
    if (!kycData.reason || kycData.reason.length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setLoading(true);
    try {
      await api.put(`/admin/v2/parties/${party.id}/kyc`, {
        kyc_level: kycData.kyc_level,
        kyc_documents: [],
        reason: kycData.reason
      });
      toast.success('KYC level updated');
      setKycFormOpen(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update KYC');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteParty = async () => {
    if (!deleteReason || deleteReason.length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setLoading(true);
    try {
      await api.delete(`/admin/v2/parties/${party.id}`, {
        data: { reason: deleteReason }
      });
      toast.success('Party deleted');
      setDeleteConfirmOpen(false);
      setDeleteReason('');
      onOpenChange(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete party');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadKYCDocument = async (documentId) => {
    try {
      const response = await api.get(`/admin/v2/parties/${party.id}/kyc-documents/${documentId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download document. File may not be available.');
    }
  };

  const handleReviewKYCDocument = async (documentId, action) => {
    const reason = action === 'approve' 
      ? 'Document verified by admin' 
      : prompt('Enter rejection reason (min 5 characters):');
    
    if (!reason || reason.length < 5) {
      if (action === 'reject') toast.error('Rejection reason required (min 5 chars)');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.put(`/admin/v2/parties/${party.id}/kyc-documents/${documentId}/review`, {
        action,
        reason
      });
      toast.success(`Document ${action}d successfully`);
      if (response.data.all_documents_approved) {
        toast.success('All KYC documents approved - party is now KYC verified!');
      }
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} document`);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewBankAccount = async (accountId, action) => {
    const reason = action === 'approve' 
      ? 'Bank account verified by admin' 
      : prompt('Enter rejection reason (min 5 characters):');
    
    if (!reason || reason.length < 5) {
      if (action === 'reject') toast.error('Rejection reason required (min 5 chars)');
      return;
    }
    
    setLoading(true);
    try {
      await api.put(`/admin/v2/parties/${party.id}/bank-accounts/${accountId}/review`, {
        action,
        reason
      });
      toast.success(`Bank account ${action}d successfully`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} bank account`);
    } finally {
      setLoading(false);
    }
  };

  // Calculate total holdings value
  const totalHoldingsValue = holdings.reduce((sum, h) => sum + (h.current_value || 0), 0);
  const totalUnits = holdings.reduce((sum, h) => sum + (h.units_held || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className={cn('p-2 sm:p-3 rounded-xl flex-shrink-0', typeConfig.color)}>
                <TypeIcon className="h-4 w-4 sm:h-6 sm:w-6" weight="duotone" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <DialogTitle className="text-base sm:text-lg truncate">{party.display_name}</DialogTitle>
                  <Badge className={cn('text-[10px] sm:text-xs', status.color)}>{status.label}</Badge>
                </div>
                <DialogDescription className="text-xs sm:text-sm truncate">
                  {party.code} • {typeConfig.label}
                </DialogDescription>
              </div>
            </div>
            {isInvestor && (
              <Button 
                variant="outline" 
                size="sm" 
                className="hidden sm:flex"
                onClick={() => {
                  onOpenChange(false);
                  onViewProfile(party.id);
                }}
                data-testid="view-investor-profile-btn"
              >
                <Eye className="h-4 w-4 mr-2" />
                Full Profile
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Quick Stats for Investors - Compact on mobile */}
        {isInvestor && (
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3 sm:mb-4">
            <Card className="p-2 sm:p-3 bg-[rgb(var(--background))]">
              <div className="flex items-center gap-1 text-foreground-muted mb-0.5 sm:mb-1">
                <Coins className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="text-[10px] sm:text-xs">Holdings</span>
              </div>
              <p className="text-sm sm:text-lg font-bold">{holdings.length}</p>
            </Card>
            <Card className="p-2 sm:p-3 bg-[rgb(var(--background))]">
              <div className="flex items-center gap-1 text-foreground-muted mb-0.5 sm:mb-1">
                <ListBullets className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="text-[10px] sm:text-xs">Units</span>
              </div>
              <p className="text-sm sm:text-lg font-bold">{totalUnits.toLocaleString()}</p>
            </Card>
            <Card className="p-2 sm:p-3 bg-[rgb(var(--background))]">
              <div className="flex items-center gap-1 text-foreground-muted mb-0.5 sm:mb-1">
                <CurrencyEur className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="text-[10px] sm:text-xs">Value</span>
              </div>
              <p className="text-sm sm:text-lg font-bold">€{totalHoldingsValue.toLocaleString()}</p>
            </Card>
          </div>
        )}

        {/* Mobile: Dropdown selector | Desktop: Tabs */}
        <div className="sm:hidden mb-3">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full text-sm" data-testid="mobile-tab-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="overview">Overview</SelectItem>
              <SelectItem value="contact">Contact</SelectItem>
              {INTERNAL_KYC_UI && (
                <>
                  <SelectItem value="verification">Verification</SelectItem>
                  <SelectItem value="financial">Banking</SelectItem>
                </>
              )}
              {isInvestor && (
                <>
                  <SelectItem value="holdings">Holdings</SelectItem>
                  <SelectItem value="transactions">Activity</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-0 sm:mt-2">
          <TabsList className={cn("hidden sm:grid w-full", INTERNAL_KYC_UI ? (isInvestor ? "grid-cols-6" : "grid-cols-4") : (isInvestor ? "grid-cols-4" : "grid-cols-2"))}>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            {INTERNAL_KYC_UI && (
              <>
                <TabsTrigger value="verification">Verification</TabsTrigger>
                <TabsTrigger value="financial">Banking</TabsTrigger>
              </>
            )}
            {isInvestor && (
              <>
                <TabsTrigger value="holdings">Holdings</TabsTrigger>
                <TabsTrigger value="transactions">Activity</TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-4">
                <h4 className="text-sm font-medium text-foreground-muted mb-3">Identity</h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-foreground-muted">Display Name</Label>
                    <p className="font-medium">{party.display_name}</p>
                  </div>
                  {party.legal_name && (
                    <div>
                      <Label className="text-xs text-foreground-muted">Legal Name</Label>
                      <p className="font-medium">{party.legal_name}</p>
                    </div>
                  )}
                  {party.identity?.first_name && (
                    <div>
                      <Label className="text-xs text-foreground-muted">First Name</Label>
                      <p className="font-medium">{party.identity.first_name}</p>
                    </div>
                  )}
                  {party.identity?.last_name && (
                    <div>
                      <Label className="text-xs text-foreground-muted">Last Name</Label>
                      <p className="font-medium">{party.identity.last_name}</p>
                    </div>
                  )}
                  {party.identity?.date_of_birth && (
                    <div>
                      <Label className="text-xs text-foreground-muted">Date of Birth</Label>
                      <p className="font-medium">{party.identity.date_of_birth}</p>
                    </div>
                  )}
                  {party.identity?.nationality && (
                    <div>
                      <Label className="text-xs text-foreground-muted">Nationality</Label>
                      <p className="font-medium">{party.identity.nationality}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-foreground-muted">Party Type</Label>
                    <Badge className={cn('text-xs mt-1', typeConfig.color)}>{typeConfig.label}</Badge>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <h4 className="text-sm font-medium text-foreground-muted mb-3">Status</h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-foreground-muted">Account Status</Label>
                    <Badge className={cn('text-xs mt-1', status.color)}>{status.label}</Badge>
                  </div>
                  {INTERNAL_KYC_UI && (
                  <div>
                    <Label className="text-xs text-foreground-muted">KYC Level</Label>
                    <Badge className={cn('text-xs mt-1', kyc.color)}>{kyc.label}</Badge>
                    {party.kyc_verified_at && (
                      <p className="text-xs text-foreground-muted mt-1">
                        Verified: {new Date(party.kyc_verified_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  )}
                  <div>
                    <Label className="text-xs text-foreground-muted">Accreditation</Label>
                    <Badge className={cn('text-xs mt-1', accreditation.color)}>{accreditation.label}</Badge>
                  </div>
                  {party.onboarding_status && (
                    <div>
                      <Label className="text-xs text-foreground-muted">Onboarding</Label>
                      <p className="font-medium capitalize">{party.onboarding_status} ({party.onboarding_progress || 0}%)</p>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {party.notes && (
              <Card className="p-4">
                <h4 className="text-sm font-medium text-foreground-muted mb-2">Internal Notes</h4>
                <p className="text-sm whitespace-pre-wrap">{party.notes}</p>
              </Card>
            )}

            {party.tags && party.tags.length > 0 && (
              <div>
                <Label className="text-xs text-foreground-muted">Tags</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {party.tags.map((tag, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="text-xs text-foreground-muted">
              Created: {new Date(party.created_at).toLocaleString()} • 
              Updated: {new Date(party.updated_at).toLocaleString()} • 
              Version: {party.version}
            </div>
          </TabsContent>

          <TabsContent value="contact" className="space-y-4 mt-4">
            <Card className="p-4">
              <h4 className="text-sm font-medium text-foreground-muted mb-3">Contact Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">Primary Email</Label>
                  <p className="font-medium flex items-center gap-1">
                    <EnvelopeSimple className="h-4 w-4" />
                    {party.contact?.email || '—'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Primary Phone</Label>
                  <p className="font-medium flex items-center gap-1">
                    <Phone className="h-4 w-4" />
                    {party.contact?.phone || '—'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Secondary Email</Label>
                  <p className="text-sm">{party.contact?.secondary_email || '—'}</p>
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Secondary Phone</Label>
                  <p className="text-sm">{party.contact?.secondary_phone || '—'}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <h4 className="text-sm font-medium text-foreground-muted mb-3">Address</h4>
              {party.address && (party.address.street_line_1 || party.address.city || party.address.country_code) ? (
                <div className="space-y-1">
                  {party.address.street_line_1 && <p>{party.address.street_line_1}</p>}
                  {party.address.street_line_2 && <p>{party.address.street_line_2}</p>}
                  {(party.address.city || party.address.state_province || party.address.postal_code) && (
                    <p>
                      {[party.address.city, party.address.state_province, party.address.postal_code]
                        .filter(Boolean).join(', ')}
                    </p>
                  )}
                  {party.address.country_code && <p className="font-medium">{party.address.country_code}</p>}
                </div>
              ) : (
                <p className="text-foreground-muted">No address on file</p>
              )}
            </Card>

            {party.website && (
              <Card className="p-4">
                <h4 className="text-sm font-medium text-foreground-muted mb-2">Website</h4>
                <a href={party.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                  <Globe className="h-4 w-4" />
                  {party.website}
                </a>
              </Card>
            )}
          </TabsContent>

          {INTERNAL_KYC_UI && (<>
          <TabsContent value="verification" className="space-y-4 mt-4">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-foreground-muted">KYC Verification</h4>
                <Button size="sm" variant="outline" onClick={() => setKycFormOpen(true)}>
                  Update KYC Level
                </Button>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">Current Level</Label>
                  <Badge className={cn('text-sm mt-1', kyc.color)}>{kyc.label}</Badge>
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Verified At</Label>
                  <p className="text-sm">{party.kyc_verified_at ? new Date(party.kyc_verified_at).toLocaleString() : '—'}</p>
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Documents</Label>
                  <p className="text-sm">{party.kyc_documents?.length || 0} documents</p>
                </div>
              </div>

              {/* KYC Documents List */}
              {party.kyc_documents && party.kyc_documents.length > 0 && (
                <div className="mt-4 pt-4 border-t border-black/[0.04]">
                  <h5 className="text-sm font-medium mb-3">Uploaded Documents</h5>
                  <div className="space-y-2">
                    {party.kyc_documents.map((doc, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white rounded-lg">
                            <FileText className="h-5 w-5 text-foreground-muted" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{doc.document_type?.replace('_', ' ').toUpperCase()}</p>
                            <p className="text-xs text-foreground-muted">{doc.filename}</p>
                            {doc.uploaded_at && (
                              <p className="text-xs text-foreground-muted">
                                Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={cn('text-xs', 
                            doc.status === 'approved' ? 'bg-green-100 text-green-700' :
                            doc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          )}>
                            {doc.status?.replace('_', ' ')}
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => handleDownloadKYCDocument(doc.id)}
                            title="Download document"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {doc.status === 'pending_review' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleReviewKYCDocument(doc.id, 'approve')}
                                title="Approve document"
                              >
                                <Check className="h-4 w-4" weight="bold" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleReviewKYCDocument(doc.id, 'reject')}
                                title="Reject document"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {kycFormOpen && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="space-y-2">
                    <Label>New KYC Level</Label>
                    <Select value={kycData.kyc_level} onValueChange={(v) => setKycData({...kycData, kyc_level: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(kycLevelConfig).map(([key, config]) => (
                          <SelectItem key={key} value={key}>{config.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Reason *</Label>
                    <Input
                      value={kycData.reason}
                      onChange={(e) => setKycData({...kycData, reason: e.target.value})}
                      placeholder="Reason for KYC level change (min 5 chars)"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleUpdateKYC} disabled={loading}>
                      {loading ? 'Saving...' : 'Update KYC'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setKycFormOpen(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            <Card className="p-4">
              <h4 className="text-sm font-medium text-foreground-muted mb-3">Accreditation</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">Status</Label>
                  <Badge className={cn('text-sm mt-1', accreditation.color)}>{accreditation.label}</Badge>
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Verified At</Label>
                  <p className="text-sm">{party.accreditation_verified_at ? new Date(party.accreditation_verified_at).toLocaleString() : '—'}</p>
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Tax Residency</Label>
                  <p className="text-sm font-medium">{party.tax_residency_country || '—'}</p>
                </div>
              </div>
            </Card>

            {party.company_registration_number && (
              <Card className="p-4">
                <h4 className="text-sm font-medium text-foreground-muted mb-3">Company Registration</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-foreground-muted">Registration Number</Label>
                    <p className="font-mono">{party.company_registration_number}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-foreground-muted">Jurisdiction</Label>
                    <p>{party.company_jurisdiction || '—'}</p>
                  </div>
                </div>
              </Card>
            )}

            {party.track_record_summary && (
              <Card className="p-4">
                <h4 className="text-sm font-medium text-foreground-muted mb-2">Track Record</h4>
                <p className="text-sm whitespace-pre-wrap">{party.track_record_summary}</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="financial" className="space-y-4 mt-4">
            <Card className="p-4">
              <h4 className="text-sm font-medium text-foreground-muted mb-3">Bank Accounts</h4>
              {party.bank_accounts && party.bank_accounts.length > 0 ? (
                <div className="space-y-3">
                  {party.bank_accounts.map((account, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{account.label}</span>
                        <div className="flex items-center gap-2">
                          <Badge className={cn('text-xs',
                            account.status === 'verified' ? 'bg-green-100 text-green-700' :
                            account.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          )}>
                            {account.status || 'pending'}
                          </Badge>
                          {account.is_primary && (
                            <Badge className="bg-primary text-white text-xs">Primary</Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-foreground-muted mb-2">
                        {account.iban && <p>IBAN: {account.iban}</p>}
                        {account.bank_name && <p>Bank: {account.bank_name}</p>}
                        {account.account_holder && <p>Holder: {account.account_holder}</p>}
                        <p>Currency: {account.currency}</p>
                      </div>
                      {account.status !== 'verified' && account.status !== 'rejected' && (
                        <div className="flex gap-2 mt-2 pt-2 border-t border-black/[0.04]">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleReviewBankAccount(account.id, 'approve')}
                          >
                            <Check className="h-4 w-4 mr-1" weight="bold" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleReviewBankAccount(account.id, 'reject')}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-foreground-muted">No bank accounts on file</p>
              )}
            </Card>

            <Card className="p-4">
              <h4 className="text-sm font-medium text-foreground-muted mb-3">Wallets</h4>
              {party.wallets && party.wallets.length > 0 ? (
                <div className="space-y-3">
                  {party.wallets.map((wallet, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{wallet.label}</span>
                        <Badge className={wallet.is_primary ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'}>
                          {wallet.is_primary ? 'Primary' : wallet.status}
                        </Badge>
                      </div>
                      <div className="text-sm text-foreground-muted">
                        <p className="font-mono text-xs break-all">{wallet.address}</p>
                        <p>Chain: {wallet.chain}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-foreground-muted">No wallets on file</p>
              )}
            </Card>
          </TabsContent>
          </>)}

          {/* Holdings Tab - For Investors Only */}
          {isInvestor && (
            <TabsContent value="holdings" className="space-y-4 mt-4">
              {loadingHoldings ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : holdings.length === 0 ? (
                <Card className="p-8 text-center">
                  <Coins className="h-10 w-10 text-foreground-muted mx-auto mb-3" />
                  <p className="text-foreground-muted">No holdings found</p>
                  <p className="text-sm text-foreground-muted/70">This investor has no ownership positions yet.</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {holdings.map((holding) => (
                    <Card key={holding.position_id} className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold">{holding.project_title}</h4>
                          <p className="text-sm text-foreground-muted">{holding.security_name}</p>
                        </div>
                        <Badge className="bg-green-100 text-green-700">
                          {holding.units_held?.toLocaleString()} units
                        </Badge>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <Label className="text-xs text-foreground-muted">Cost Basis</Label>
                          <p className="font-medium">€{holding.cost_basis?.toLocaleString()}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-foreground-muted">Current Value</Label>
                          <p className="font-medium">€{holding.current_value?.toLocaleString()}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-foreground-muted">Unrealized Gain</Label>
                          <p className={cn("font-medium", holding.unrealized_gain >= 0 ? "text-green-600" : "text-red-600")}>
                            {holding.unrealized_gain >= 0 ? '+' : ''}€{holding.unrealized_gain?.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs text-foreground-muted">Holding Period</Label>
                          <p className="font-medium">{holding.holding_days || 0} days</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {/* Transactions Tab - For Investors Only */}
          {isInvestor && (
            <TabsContent value="transactions" className="space-y-4 mt-4">
              {loadingTx ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : transactions.length === 0 ? (
                <Card className="p-8 text-center">
                  <ChartLineUp className="h-10 w-10 text-foreground-muted mx-auto mb-3" />
                  <p className="text-foreground-muted">No transactions found</p>
                  <p className="text-sm text-foreground-muted/70">This investor has no cap table activity yet.</p>
                </Card>
              ) : (
                <div className="space-y-2">
                  {transactions.map((tx) => (
                    <Card key={tx.id} className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "p-2 rounded-lg",
                            tx.event_type === 'mint' || tx.event_type === 'allotment' ? "bg-green-100" : "bg-blue-100"
                          )}>
                            {tx.event_type === 'mint' ? (
                              <Plus className="h-4 w-4 text-green-600" />
                            ) : tx.event_type === 'transfer' ? (
                              <ArrowsClockwise className="h-4 w-4 text-blue-600" />
                            ) : (
                              <Coins className="h-4 w-4 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{tx.event_type?.replace('_', ' ').toUpperCase()}</p>
                            <p className="text-xs text-foreground-muted">{tx.project_title} • {tx.security_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-sm">
                            {tx.direction === 'in' ? '+' : '-'}{tx.units?.toLocaleString()} units
                          </p>
                          {tx.effective_date && (
                            <p className="text-xs text-foreground-muted">
                              {new Date(tx.effective_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Trash className="h-5 w-5" />
                Delete Party
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{party.display_name}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-700">
                  <strong>Note:</strong> Parties with active holdings, subscriptions, or linked projects cannot be deleted.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Reason for deletion *</Label>
                <Input
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Why is this party being deleted? (min 5 chars)"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDeleteConfirmOpen(false); setDeleteReason(''); }}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteParty}
                disabled={loading || deleteReason.length < 5}
              >
                {loading ? 'Deleting...' : 'Delete Party'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            {party.status === 'pending_verification' && (
              <Button onClick={handleActivate} disabled={loading} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-1" />
                Activate Party
              </Button>
            )}
            <Button 
              variant="outline" 
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => setDeleteConfirmOpen(true)}
              data-testid="delete-party-btn"
            >
              <Trash className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onEdit?.(party)}>
              <PencilSimple className="h-4 w-4 mr-1" />
              Edit
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main Party Management Page
 */
export default function AdminPartiesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [parties, setParties] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filterType, setFilterType] = useState(searchParams.get('type') || 'all');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [filterKyc, setFilterKyc] = useState('all');
  const [sort, setSort] = useState('newest');
  const [viewMode, setViewMode] = useState('card');
  
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedParty, setSelectedParty] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editParty, setEditParty] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterType !== 'all') params.append('party_type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const [partiesRes, statsRes] = await Promise.all([
        api.get(`/admin/v2/parties?${params}`),
        api.get('/admin/v2/parties/stats')
      ]);
      
      setParties(partiesRes.data.parties);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load parties');
    } finally {
      setLoading(false);
    }
  }, [search, filterType, filterStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const visibleParties = useMemo(() => {
    let list = parties.filter((p) => (filterKyc === 'all' ? true : (p.kyc_level || 'none') === filterKyc));
    if (sort === 'name') list = [...list].sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''));
    return list;
  }, [parties, filterKyc, sort]);

  const handlePartyClick = async (party) => {
    try {
      const response = await api.get(`/admin/v2/parties/${party.id}`);
      setSelectedParty(response.data);
      setDetailOpen(true);
    } catch (err) {
      toast.error('Failed to load party details');
    }
  };

  const handleEditParty = (party) => {
    setEditParty(party);
    setDetailOpen(false);
  };

  return (
    <div className="max-w-[1200px] space-y-6" data-testid="admin-parties-page">
      <AdminPageHeader
        eyebrow="Relationships"
        title="Investors & parties"
        subtitle="Investors, developers and sponsors, with their verification status."
        actions={(
          <Button onClick={() => setCreateOpen(true)} size="sm" data-testid="create-party-btn">
            <Plus className="h-4 w-4 md:mr-1.5" />
            <span className="hidden md:inline">New party</span>
          </Button>
        )}
      />

      {stats && (
        <AdminKpiStrip>
          <AdminKpi label="Total parties" value={stats.total} testId="parties-kpi-total" />
          <AdminKpi label="Active" value={stats.total_active} testId="parties-kpi-active" />
          {INTERNAL_KYC_UI && (
            <AdminKpi label="Pending KYC" value={stats.total_pending} testId="parties-kpi-pending" />
          )}
          <AdminKpi
            label="Developers"
            value={stats.total_developers ?? 0}
            testId="parties-kpi-developers"
          />
        </AdminKpiStrip>
      )}


      <AdminToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search by name, code or email…"
        filters={[
          { key: 'type', label: 'Type', options: Object.entries(partyTypeConfig).map(([value, c]) => ({ value, label: c.label })) },
          ...(INTERNAL_KYC_UI ? [{ key: 'kyc', label: 'KYC', options: [
            { value: 'none', label: 'Not started' },
            { value: 'basic', label: 'Basic' },
            { value: 'verified', label: 'Verified' },
            { value: 'enhanced', label: 'Enhanced' },
          ] }] : []),
          { key: 'status', label: 'Status', options: Object.entries(statusConfig).map(([value, c]) => ({ value, label: c.label })) },
        ]}
        values={{ type: filterType, kyc: filterKyc, status: filterStatus }}
        onFilter={(key, value) => {
          if (key === 'type') setFilterType(value);
          else if (key === 'kyc') setFilterKyc(value);
          else setFilterStatus(value);
        }}
        onClear={() => { setFilterType('all'); setFilterKyc('all'); setFilterStatus('all'); }}
        sortOptions={[
          { value: 'newest', label: 'Newest first' },
          { value: 'name', label: 'Name A-Z' },
        ]}
        sort={sort}
        onSort={setSort}
        view={viewMode}
        onView={setViewMode}
        resultLabel={`${visibleParties.length} of ${parties.length} parties`}
      />

        {/* Parties List - Single column on mobile */}
        {loading ? (
          <div className="flex items-center justify-center py-12 sm:py-16">
            <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : visibleParties.length === 0 ? (
          <Card className="bg-white p-8 sm:p-12 text-center rounded-xl border border-black/[0.04]">
            <Users className="h-10 w-10 sm:h-12 sm:w-12 text-foreground-muted mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium mb-2">No parties found</h3>
            <p className="text-sm text-foreground-muted mb-4">
              {search || filterType !== 'all' || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first party to get started'}
            </p>
            <Button onClick={() => setCreateOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Create Party
            </Button>
          </Card>
        ) : viewMode === 'list' ? (
          <Card className="bg-white rounded-xl border border-black/[0.04] overflow-hidden" data-testid="parties-list-view">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-black/[0.08] bg-slate-50">
                    <th className="text-left py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Party</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Type</th>
                    {INTERNAL_KYC_UI && (
                      <th className="text-left py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">KYC</th>
                    )}
                    <th className="text-left py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Status</th>
                    <th className="text-right py-2.5 px-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleParties.map((party) => (
                    <tr
                      key={party.id}
                      onClick={() => handlePartyClick(party)}
                      className="border-b border-black/[0.04] hover:bg-slate-50 cursor-pointer transition-colors"
                      data-testid={`party-row-${party.code}`}
                    >
                      <td className="py-3 px-4">
                        <span className="font-mono text-[10px] text-foreground-muted block">{party.code}</span>
                        <span className="text-sm font-medium">{party.display_name}</span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {partyTypeConfig[party.party_type]?.label || party.party_type}
                      </td>
                      {INTERNAL_KYC_UI && (
                        <td className="py-3 px-4 text-sm capitalize">{party.kyc_level || 'none'}</td>
                      )}
                      <td className="py-3 px-4 text-sm capitalize">{party.status}</td>
                      <td className="py-3 px-4 text-right text-xs text-foreground-muted">
                        {party.created_at ? new Date(party.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {visibleParties.map((party) => (
              <PartyCard key={party.id} party={party} onClick={handlePartyClick} />
            ))}
          </div>
      )}

      <PartyFormDialog
        open={createOpen || !!editParty}
        onOpenChange={(open) => {
          if (!open) {
            setCreateOpen(false);
            setEditParty(null);
          }
        }}
        party={editParty}
        onSave={() => {
          fetchData();
          setEditParty(null);
        }}
      />

      <PartyDetailDialog
        party={selectedParty}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onRefresh={async () => {
          if (selectedParty) {
            const response = await api.get(`/admin/v2/parties/${selectedParty.id}`);
            setSelectedParty(response.data);
          }
          fetchData();
        }}
        onViewProfile={(partyId) => navigate(`/admin/investor/${partyId}`)}
        onEdit={(party) => {
          setDetailOpen(false);
          setEditParty(party);
        }}
      />
    </div>
  );
}
